// Color palette for category visualizations. Categories come from the user's
// spreadsheet (free-form strings), so we deterministically map any string to
// a color. The palette is interleaved across the hue wheel so that
// alphabetically adjacent categories (which is the order assignCategoryColors
// uses) land on visually distinct hues.
//
// Each entry is a CSS custom property defined in apps/web/src/index.css —
// the design-system tokens are the single source of truth for color values
// across the app. Don't inline hex literals here; if a hue isn't in the
// extended palette block of index.css, add it there first.
//
// Purple slots are spaced 5 apart and use *distinct* purple hues — pure
// violet, blue-leaning indigo, magenta-leaning fuchsia, redder purple — so
// that two adjacent categories never get assigned hard-to-distinguish
// purples (e.g. crypto vs. stocks).
//
// Last-resort hues sit at the end and only get used after the primary
// palette is exhausted:
//   - blue/sky near Dakota's identity color and pink hues near Natalia's
//     (so charts don't read as a person-scoped view)
//   - violet-400 and violet-600, which are just lighter/darker versions of
//     violet-500 and would clash with it perceptually
const PALETTE = [
  'var(--berg-orange-bold)',
  'var(--berg-green-bold)',
  'var(--berg-yellow)',
  'var(--berg-violet)',     // purple #1 (pure)
  'var(--berg-cyan)',
  'var(--berg-red-bold)',
  'var(--berg-lime)',
  'var(--berg-amber)',
  'var(--berg-indigo)',     // purple #2 (blue-leaning)
  'var(--berg-emerald)',
  'var(--berg-orange)',
  'var(--berg-cyan-light)',
  'var(--berg-teal)',
  'var(--berg-fuchsia)',    // purple #3 (magenta-leaning)
  'var(--berg-amber-light)',
  'var(--berg-emerald-light)',
  'var(--berg-rose)',
  'var(--berg-cyan-dark)',
  'var(--berg-purple)',     // purple #4 (redder purple)
  'var(--berg-lime-dark)',
  'var(--berg-rose-light)',
  // Last-resort: visual duplicates or person-scoped hues.
  'var(--berg-violet-dark)',
  'var(--berg-violet-light)',
  'var(--berg-blue)',
  'var(--berg-sky)',
  'var(--berg-pink)',
  'var(--berg-pink-light)',
  'var(--berg-pink-dark)',
];

const FALLBACK_COLOR = 'var(--berg-neutral)';

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

// Matches an emoji (or other extended pictographic) at the start of a string,
// optionally followed by a variation selector (U+FE0F) and any whitespace.
// `\p{Extended_Pictographic}` covers the full set of emoji codepoints; the
// trailing `️?` absorbs the variation selector so the captured group
// is the bare displayable glyph.
const LEADING_EMOJI = /^(\p{Extended_Pictographic}️?)\s*/u;

export function splitCategoryEmoji(name: string): { emoji: string | null; name: string } {
  const m = LEADING_EMOJI.exec(name);
  if (!m) return { emoji: null, name };
  return { emoji: m[1], name: name.slice(m[0].length) };
}

export function stripCategoryEmoji(name: string): string {
  return splitCategoryEmoji(name).name;
}

export function getCategoryEmoji(name: string): string | null {
  return splitCategoryEmoji(name).emoji;
}

// Fixed color assignments per category. Keys are bare names (no emoji
// prefix); lookups strip emoji so "🍔 Food & Beverages" matches.
const CATEGORY_COLORS: Record<string, string> = {
  // Cash Out parent categories
  Debt: 'var(--berg-violet-light)',
  'Discretionary & Lifestyle': 'var(--berg-pink-light)',
  'Food & Beverages': 'var(--berg-yellow)',
  Healthcare: 'var(--berg-lime)',
  'Home & Pets': 'var(--berg-cyan)',
  'Professional Services': 'var(--berg-teal)',
  'Savings & Investments': 'var(--berg-amber)',
  'Transportation & Travel': 'var(--berg-indigo)',
  // Code-only categories
  Income: 'var(--berg-green)',
  'Tax Return': 'var(--berg-green)',
  Interest: 'var(--berg-green)',
  Transfer: 'var(--berg-neutral)',
  Excluded: 'var(--berg-neutral)',
  // Debt sub-categories
  'Mortgages & Equity Loans': 'var(--berg-cyan)',
  'Auto Loans': 'var(--berg-orange)',
  'Student Loans': 'var(--berg-yellow)',
  'Credit Cards': 'var(--berg-lime)',
  'Short Term Loans': 'var(--berg-rose-light)',
  'Family & Friends Loans': 'var(--berg-iris)',
  // Asset categories
  'Cash & Sinking Funds': 'var(--berg-amber)',
  Cryptocurrency: 'var(--berg-lime)',
  'Stocks & Index Funds': 'var(--berg-cyan)',
  'Equity': 'var(--berg-purple)',
  'Real Estate': 'var(--berg-pink-light)',
  'Retirement & Long Term Savings': 'var(--berg-amber-light)',
  Vehicles: 'var(--berg-blue)',
  // Retirement Account Mix donut
  'Roth 401K': 'var(--berg-yellow)',
  '401K': 'var(--berg-amber)',
  'Roth IRA': 'var(--berg-fuchsia)',
  IRA: 'var(--berg-purple)',
  Stocks: 'var(--berg-cyan)',
  'Private Stocks & Equity': 'var(--berg-rose-light)',
  'Emergency Fund': 'var(--berg-indigo)',
  'Health Savings Account': 'var(--berg-emerald)',
  // Retirement projection breakdown donut
  Contributions: 'var(--berg-purple)',
  Gains: 'var(--berg-green)',
  // Segmented-bar buckets (code-derived, not sheet categories)
  Productive: 'var(--berg-violet-light)',
  Consumer: 'var(--berg-red)',
  Other: 'var(--berg-orange)',
  Liquid: 'var(--berg-green)',
  'Long-term': 'var(--berg-purple)',
  Fixed: 'var(--berg-neutral-soft)',
  // Income page identities (take-home + tax breakdown)
  'Take-home': 'var(--berg-green)',
  Federal: 'var(--berg-red)',
  FICA: 'var(--berg-orange)',
  State: 'var(--berg-yellow)',
  // Combined tax slice used when the per-tax-type breakdown is collapsed to
  // a single "Taxes" bucket (per-source donut on the Income page). Subtler
  // than the per-type colors so the take-home slice stays the focal point.
  Taxes: 'var(--berg-neutral-light)',
  // Budget page identities (monthly outgoing / remaining bar)
  Expenses: 'var(--berg-rose-light)',
  Outgoing: 'var(--berg-neutral-faint)',
  Remaining: 'var(--berg-green)',
  // Income sources
  GridStrong: 'var(--berg-dakota)',
  'Freelance Design': 'var(--berg-dakota)',
  'Barclay Damon': 'var(--berg-natalia)',
  FAFSA: 'var(--berg-natalia)',
};

// Categories that exist only in code (not in the Cash Out sheet). Plaid's
// `INCOME_*` PFCs auto-map to these so paychecks and refunds get a chip
// instead of showing as Uncategorized; the recategorize modal also exposes
// them as standalone options. Stored values include the emoji prefix to
// match the canonical-name convention sheet categories use.
//
// Keep PFC_CATEGORY_MAP in apps/web/api/_lib/categorization.ts in sync with
// these literals (the API can't import @budget/core at runtime).
export const CODE_ONLY_CATEGORIES = [
  '💰 Income',
  '📋 Tax Return',
  '📈 Interest',
  '🔄 Transfer',
  '🚫 Excluded',
] as const;

// Categories whose transactions must NOT contribute to budget spend
// totals. Two reasons a row lands here:
//   1. Transfer  — money moved between accounts the household already owns
//                  (credit-card payment, account transfer); the real spend
//                  was counted at the originating side. Also covers
//                  sinking-fund outflows pre-funded by a smoothed monthly
//                  contribution to a vault (budget impact already counted
//                  there) — these used to live under a separate "Sinking
//                  Fund Spend" category, since merged into Transfer.
//   2. Excluded  — user-marked "skip this one" — for transactions that
//                  don't fit the above but shouldn't count (reimbursable
//                  expenses, money you fronted for someone, one-off
//                  pass-throughs, etc.).
// The chip still renders in the transactions list for audit; the
// aggregate=totals API filters them out.
//
// NOTE: the budget-totals SQL in apps/web/api/transactions/_list.ts
// hardcodes these literals (it can't import @budget/core at runtime).
// Any change here must be mirrored there.
export const TRANSFER_CATEGORIES = new Set<string>([
  '🔄 Transfer',
  '🚫 Excluded',
]);

export function getCategoryColor(category?: string | null): string {
  if (!category) return FALLBACK_COLOR;
  const bare = stripCategoryEmoji(category);
  const fixed = CATEGORY_COLORS[bare];
  if (fixed) return fixed;
  return PALETTE[hashString(bare) % PALETTE.length];
}

export function assignCategoryColors(
  categories: readonly (string | null | undefined)[],
  // Reserved colors that should never be auto-assigned. Useful when the
  // surrounding card already uses certain hues for fixed semantics
  // (e.g. the income page uses green/red/orange/yellow on the take-home
  // and tax breakdowns) and you don't want auto-categorized slices to
  // collide with them.
  excludeColors: readonly string[] = [],
): Map<string, string> {
  // Pre-seed with every fixed mapping (bare keys) so callers can look up any
  // known category — including Plaid PFC fallback labels — even when the
  // category isn't in the input set.
  const result = new Map<string, string>(Object.entries(CATEGORY_COLORS));
  const blocked = new Set(excludeColors);
  const palette = blocked.size === 0 ? PALETTE : PALETTE.filter((c) => !blocked.has(c));
  // Sort by the bare name so palette assignment is stable when a sheet
  // category gains or loses an emoji prefix.
  const unique = Array.from(
    new Set(categories.filter((c): c is string => Boolean(c)))
  ).sort((a, b) => stripCategoryEmoji(a).localeCompare(stripCategoryEmoji(b)));
  unique.forEach((cat, i) => {
    if (result.has(cat)) return;
    const bare = stripCategoryEmoji(cat);
    const fixed = CATEGORY_COLORS[bare];
    if (fixed && !blocked.has(fixed)) {
      result.set(cat, fixed);
    } else {
      result.set(cat, palette[i % palette.length]);
    }
  });
  return result;
}

export function getCategoryDisplayName(category?: string | null): string {
  if (!category) return 'Uncategorized';
  return category;
}
